import { FC, ReactNode, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { motion } from 'framer-motion';

interface TooltipProps {
  content: string;
  children: ReactNode;
}

/**
 * Tooltip component that displays a floating tooltip when hovering over the child element.
 * The tooltip is dynamically positioned above the target element and centered horizontally relative to it.
 * The tooltip's visibility is animated using `framer-motion`.
 *
 * @param {TooltipProps} props - The properties passed to the Tooltip component.
 * @param {string} props.content - The content to be displayed inside the tooltip.
 * @param {ReactNode} props.children - The child element that triggers the tooltip when hovered over.
 *
 */

const Tooltip: FC<TooltipProps> = ({ content, children }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });

  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (visible && targetRef.current && tooltipRef.current) {
      const targetRect = targetRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const top = targetRect.top - tooltipRect.height - 10;
      const left =
        targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
      setPosition({ top, left });
    }
  }, [visible]);

  return (
    <div
      className="relative"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      ref={targetRef}>
      {children}

      {visible &&
        createPortal(
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="absolute bg-[#BDE0F5E5]/90 text-[#121212] text-[15px] font-jostRegular px-3 py-1 rounded-lg shadow-md opacity-100 transition-opacity duration-200 w-[300px] text-center border border-[#1F2B40] z-[9999]"
            style={{
              top: `${position.top}px`,
              left: `${position.left}px`,
            }}
            ref={tooltipRef}>
            {content}
          </motion.div>,
          document.body
        )}
    </div>
  );
};

export default Tooltip;
